import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "tom:donation-popup-seen";

const DonationPopup = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Respect users who have already dismissed/seen it this session
    if (sessionStorage.getItem(STORAGE_KEY) === "1") return;

    const timer = window.setTimeout(() => setOpen(true), 9000);

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handleKey);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("keydown", handleKey);
    };
  }, []);

  const handleClose = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore storage errors */
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="donation-popup-title"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close donation popup"
        onClick={handleClose}
        className="absolute inset-0 h-full w-full cursor-default bg-background/70 backdrop-blur-sm animate-[fade-in_0.4s_ease-out]"
      />

      {/* Card */}
      <div
        className="relative z-10 w-full max-w-lg overflow-hidden rounded-3xl border border-border/60 bg-card shadow-[0_30px_80px_-20px_hsl(var(--primary)/0.35)] animate-[pop-in_0.5s_cubic-bezier(0.16,1,0.3,1)]"
      >
        {/* Ambient glow */}
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,hsl(var(--primary)/0.12),transparent_60%)]"
          aria-hidden="true"
        />
        {/* Top accent line */}
        <div
          className="h-px w-full bg-gradient-to-r from-transparent via-primary/50 to-transparent"
          aria-hidden="true"
        />

        {/* Close */}
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/60 bg-background/80 text-muted-foreground transition-colors duration-300 hover:text-foreground hover:border-primary/40"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        <div className="relative z-[1] px-8 py-10 text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-5 font-heading">
            Fuel the Movement
          </p>

          <h2
            id="donation-popup-title"
            className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight leading-tight"
          >
            Your mind is different.
            <br />
            It should not have to work alone.
          </h2>

          <p className="text-base text-muted-foreground leading-relaxed mb-8 max-w-md mx-auto">
            The Octopus Movement connects 12,000 nonlinear thinkers across the world. Your monthly
            donation keeps those connections possible.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/donate"
              onClick={handleClose}
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-heading font-semibold uppercase tracking-wider text-primary-foreground shadow-[0_8px_24px_-6px_hsl(var(--primary)/0.55)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_-6px_hsl(var(--primary)/0.65)]"
            >
              Become a Mycelium Supporter
              <span aria-hidden="true">→</span>
            </Link>
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex w-full sm:w-auto items-center justify-center rounded-full px-7 py-3.5 text-sm font-heading font-medium uppercase tracking-wider text-muted-foreground transition-colors duration-300 hover:text-foreground"
            >
              Maybe later
            </button>
          </div>

          <p className="mt-6 text-[11px] text-muted-foreground/80">
            Monthly tiers start at $8. Pause or cancel anytime, no questions asked.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DonationPopup;
